import { featureFlags } from "~/common/feature-flags";
import { fetchWithAuth } from "~/common/fetch";
import { flushPendingPhotoUploads } from "~/common/pending-photo-uploads";
import { formSubmitNotification } from "~/components/notifications/form_submit_pop";
import { saveDraftPatientNotification } from "~/components/notifications/save_patient_draft";
import {
  getPendingAudioUpload,
  removePendingAudioUpload,
} from "~/components/patient/patient_new/audio_pending_uploads";
import type { TAligner } from "~/components/patient/patient_new/patient_new_additional_aligners";
import {
  API_POST_PATIENT,
  API_PUT_PATIENT,
  API_PUT_UNCOMPLETE_PATIENT,
  API_SUBMIT_PATIENT,
} from "~/config";
import type { TUserActionNotification } from "~/reducers/dashboard";
import type { TInstructions } from "~/slices/instructions";
import type { AppDispatch, RootState } from "~/store";
import type { TPatient } from "~/types/patient";
import type { TPrescription } from "~/types/prescription";

import { addUserActionNotification } from "./user_notification";

export function patientIsSavingSuccess(bool: boolean) {
  return { type: "PATIENT_SAVE_SUCCESSFULL", patientIsSaving: bool } as const;
}

export type TCreatePatientInstructions = Partial<
  TInstructions & {
    prescription: TPrescription;
    links: string[];
    additional_aligners?: TAligner[];
  }
>;

type AudioUploadItem = NonNullable<NonNullable<TInstructions["audio_note"]>["uploads"]>[number];

function isResolvedAudioUpload(upload: AudioUploadItem): boolean {
  if (
    typeof upload.storage_resource_id === "number" &&
    Number.isFinite(upload.storage_resource_id)
  ) {
    return true;
  }

  return Boolean(upload.uuid && upload.file_name);
}

function sanitizeAudioNoteForDraft(
  audioNote: TCreatePatientInstructions["audio_note"],
): TCreatePatientInstructions["audio_note"] | undefined {
  if (!audioNote) {
    return audioNote;
  }

  const resolvedUploads = (audioNote.uploads ?? []).filter(isResolvedAudioUpload);
  if (resolvedUploads.length === 0 && !audioNote.storage_resource_id) {
    return undefined;
  }

  return {
    ...audioNote,
    uploads: resolvedUploads,
  };
}

async function requestAudioUploadLink(
  file: File,
  clinicId?: number,
): Promise<{ uuid: string; uploadUrl: string }> {
  const response = await fetchWithAuth(`${process.env.API}/patients/media`, {
    method: "POST",
    body: JSON.stringify({
      type: "audio",
      file_name: file.name,
      clinic_id: clinicId ?? null,
    }),
  });
  if (!response.ok) {
    throw new Error("Unable to prepare audio upload");
  }

  const payload = (await response.json()) as { uuid: string; upload_link: string };
  return { uuid: payload.uuid, uploadUrl: payload.upload_link };
}

async function uploadAudioFile(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  });

  if (!response.ok) {
    throw new Error("Unable to upload audio file");
  }
}

async function resolveAudioNoteForSubmit(
  audioNote: TCreatePatientInstructions["audio_note"],
  clinicId?: number,
): Promise<TCreatePatientInstructions["audio_note"] | undefined> {
  if (!audioNote) {
    return audioNote;
  }

  const uploads = audioNote.uploads ?? [];
  if (uploads.length === 0) {
    return audioNote;
  }

  const resolvedUploads: AudioUploadItem[] = [];
  for (const upload of uploads) {
    if (isResolvedAudioUpload(upload)) {
      resolvedUploads.push(upload);
      continue;
    }

    if (!upload.client_id) {
      continue;
    }

    const pendingFile = getPendingAudioUpload(upload.client_id);
    if (!pendingFile) {
      continue;
    }

    const { uuid, uploadUrl } = await requestAudioUploadLink(pendingFile, clinicId);
    await uploadAudioFile(uploadUrl, pendingFile);
    removePendingAudioUpload(upload.client_id);

    resolvedUploads.push({
      ...upload,
      uuid,
      file_name: pendingFile.name,
    });
  }

  if (resolvedUploads.length === 0 && !audioNote.storage_resource_id) {
    return undefined;
  }

  return {
    ...audioNote,
    uploads: resolvedUploads,
  };
}

/**
 * Убирает `additional_service_tags` из тела запроса, когда платный блок
 * «Дополнительные услуги» выключен фиче-флагом.
 *
 * Это единственная гарантия контракта, а не «на всякий случай»: ключ попадает в
 * `instructions` не только из чекбоксов (их с выключенным флагом нет), но и из
 * `PatientUpdate.componentDidUpdate`, который на форме редактирования подтягивает теги
 * из уже сохранённого пациента. Прод-бэкенд (master) про этот ключ не знает и отбивает
 * ВЕСЬ запрос 400-й — `Patient.basic_requirements_satisfied` не пропускает ничего за
 * пределами `modifiable ∪ requirements` (кейс 38247, «не получается подписать ДС»).
 *
 * Обе функции ниже — единственные точки сборки payload'а для create/update/submit
 * (`updateMedia` шлёт только media), поэтому фильтр здесь закрывает все четыре пути.
 */
function stripDisabledAdditionalServiceTags(
  instructions: TCreatePatientInstructions,
): TCreatePatientInstructions {
  if (featureFlags.additionalPaidServices) {
    return instructions;
  }

  const { additional_service_tags: _, ...rest } = instructions;
  return rest as TCreatePatientInstructions;
}

function prepareInstructionsForDraft(
  instructions: TCreatePatientInstructions,
): Promise<TCreatePatientInstructions> {
  const sanitizedAudioNote = sanitizeAudioNoteForDraft(instructions.audio_note);
  return Promise.resolve({
    ...stripDisabledAdditionalServiceTags(instructions),
    ...(sanitizedAudioNote ? { audio_note: sanitizedAudioNote } : { audio_note: undefined }),
  });
}

async function prepareInstructionsForSubmit(
  instructions: TCreatePatientInstructions,
): Promise<TCreatePatientInstructions> {
  const resolvedAudioNote = await resolveAudioNoteForSubmit(
    instructions.audio_note,
    instructions.clinic_id,
  );
  return {
    ...stripDisabledAdditionalServiceTags(instructions),
    ...(resolvedAudioNote ? { audio_note: resolvedAudioNote } : { audio_note: undefined }),
  };
}

/**
 * The draft was created, but submitting it failed. The form keeps `patientId` so the next
 * attempt updates this draft instead of creating a second patient.
 */
export class PatientSubmitError extends Error {
  constructor(
    readonly patientId: TPatient["patient_id"],
    cause: unknown,
  ) {
    super("Error trying to submit patient", { cause });
  }
}

export function createAndSubmitPatient(
  instructions: TCreatePatientInstructions,
  media: RootState["media"],
  media_s3: RootState["media_s3"],
) {
  const NOTIFICATION_MSG_OK: TUserActionNotification = {
    message: "notify.patient.create.ok",
    level: "success",
    position: "tc",
    autoDismiss: 10,
    dismissible: false,
    children: formSubmitNotification(),
  };

  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.patient.create.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (await flushPendingPhotoUploads()) media = getState().media;
      const payloadInstructions = await prepareInstructionsForSubmit(instructions);
      const response = await fetchWithAuth(API_POST_PATIENT, {
        method: "POST",
        body: JSON.stringify({ ...payloadInstructions, media_info: media, s3_media: media_s3 }),
      });
      if (!response.ok) {
        throw new Error("Error trying to create patient");
      }

      let json = (await response.json()) as Pick<
        TPatient,
        "first_name" | "last_name" | "patient_id"
      >;

      try {
        const response_2 = await fetchWithAuth(API_SUBMIT_PATIENT(json.patient_id), {
          method: "POST",
        });
        if (!response_2.ok) {
          throw new Error("Error trying to submit patient");
        }

        json = (await response_2.json()) as Pick<
          TPatient,
          "first_name" | "last_name" | "patient_id"
        >;
      } catch (err) {
        throw new PatientSubmitError(json.patient_id, err);
      }

      // NOTE: The timeout is needed because we want to render the notification after we
      // have been redirected to the patients page. Otherwise incorrect styles are applied to the popup
      // and it appears at the top of the screen, half invisible.
      window.setTimeout(() => dispatch(addUserActionNotification(NOTIFICATION_MSG_OK)));
      dispatch({ type: "ADD_PATIENT_SUCCESSFULL", json });
    } catch (err) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw err;
    }
  };
}

export function createPatient(
  instructions: TCreatePatientInstructions,
  media: RootState["media"],
  media_s3: RootState["media_s3"],
) {
  const NOTIFICATION_MSG_OK: TUserActionNotification = {
    message: "notify.patient.create.draft.ok",
    level: "success",
    position: "tc",
    autoDismiss: 0,
    dismissible: false,
    children: saveDraftPatientNotification(),
  };

  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.patient.create.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (await flushPendingPhotoUploads()) media = getState().media;
      const payloadInstructions = await prepareInstructionsForDraft(instructions);
      const response = await fetchWithAuth(API_POST_PATIENT, {
        method: "POST",
        body: JSON.stringify({ ...payloadInstructions, media_info: media, s3_media: media_s3 }),
      });
      if (!response.ok) {
        throw new Error("Error occurred trying to create patient");
      }
      const json = await response.json();

      // NOTE: The timeout is needed because we want to render the notification after we
      // have been redirected to the patients page. Otherwise incorrect styles are applied to the popup
      // and it appears at the top of the screen, half invisible.
      window.setTimeout(() => dispatch(addUserActionNotification(NOTIFICATION_MSG_OK)));
      dispatch({ type: "ADD_PATIENT_SUCCESSFULL", json });
    } catch (err) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw err;
    }
  };
}

export function updatePatient(
  patient_id: TPatient["patient_id"],
  {
    instructions,
    media,
    media_s3,
    replaceLinks = false,
  }: {
    instructions: TCreatePatientInstructions;
    media: RootState["media"];
    media_s3: RootState["media_s3"];
    replaceLinks?: boolean;
  },
) {
  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.patient.update.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (await flushPendingPhotoUploads()) media = getState().media;
      const payloadInstructions = await prepareInstructionsForDraft(instructions);
      const response = await fetchWithAuth(API_PUT_UNCOMPLETE_PATIENT(patient_id), {
        method: "PUT",
        body: JSON.stringify({ ...payloadInstructions, media_info: media, s3_media: media_s3, ...(replaceLinks ? { replace_links: true } : {}) }),
      });
      if (!response.ok) {
        throw new Error("Error occurred trying to update patient");
      }
    } catch (err) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw err;
    }
  };
}

export function updateAndSubmitPatient(
  patient_id: TPatient["patient_id"],
  {
    instructions,
    media,
    media_s3,
    replaceLinks = false,
  }: {
    instructions: TCreatePatientInstructions;
    media: RootState["media"];
    media_s3: RootState["media_s3"];
    replaceLinks?: boolean;
  },
) {
  const NOTIFICATION_MSG_OK: TUserActionNotification = {
    message: "notify.patient.create.ok",
    level: "success",
    position: "tc",
    autoDismiss: 10,
    dismissible: false,
    children: formSubmitNotification(),
  };

  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.patient.update.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (await flushPendingPhotoUploads()) media = getState().media;
      const payloadInstructions = await prepareInstructionsForSubmit(instructions);
      const response = await fetchWithAuth(API_PUT_PATIENT(patient_id), {
        method: "PUT",
        body: JSON.stringify({ ...payloadInstructions, media_info: media, s3_media: media_s3, ...(replaceLinks ? { replace_links: true } : {}) }),
      });
      if (!response.ok) {
        throw new Error("Error occurred trying to update patient");
      }
      const json = (await response.json()) as Pick<TPatient, "patient_id">;

      const response_2 = await fetchWithAuth(API_SUBMIT_PATIENT(json.patient_id), {
        method: "POST",
      });
      if (!response_2.ok) {
        throw new Error("Error occurred trying to submit patient");
      }

      // NOTE: The timeout is needed because we want to render the notification after we
      // have been redirected to the patients page. Otherwise incorrect styles are applied to the popup
      // and it appears at the top of the screen, half invisible.
      window.setTimeout(() => dispatch(addUserActionNotification(NOTIFICATION_MSG_OK)));
    } catch (err) {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
      throw err;
    }
  };
}

export function updateMedia(
  patient_id: TPatient["patient_id"],
  {
    media,
    media_s3,
    links = null,
  }: {
    media: RootState["media"];
    media_s3: RootState["media_s3"];
    links?: string[] | null;
  },
) {
  const instructions: {
    media_info: RootState["media"];
    s3_media: RootState["media_s3"];
    links: string[];
  } = {
    media_info: media,
    s3_media: media_s3,
    links: links || [],
  };

  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchWithAuth(API_PUT_PATIENT(patient_id), {
        method: "PUT",
        body: JSON.stringify(instructions),
      });
      if (!response.ok) {
        throw new Error("Error occurred tryint to update media");
      }
      const json = await response.json();
      dispatch({ type: "UPDATE_PATIENT_SUCCESSFULL", json });
    } catch (error) {
      console.error("Error updating media:", error);
      throw error;
    }
  };
}
